import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { collection, doc, getDocs, onSnapshot, query, where, limit } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase/config.js';
import { useAuth } from '../auth/AuthProvider.jsx';
import { getSubdomainGymSlug } from '../lib/subdomain.js';

const DEFAULT_BRAND = {
  primary: '#0E0E10',
  accent: '#F2F2F2',
  fontDisplay: 'Inter',
  logoUrl: null,
  tagline: '',
  name: 'TrainForge',
};

const BRAND_CACHE_KEY = 'tf:subdomainBrand';

function loadCachedSubdomainBrand(slug) {
  if (!slug || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(BRAND_CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    return cached?.slug === slug ? cached.gym : null;
  } catch { return null; }
}

function saveCachedSubdomainBrand(slug, gym) {
  if (!slug || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify({ slug, gym }));
  } catch { /* ignore */ }
}

const TenantCtx = createContext({ gymId: null, gym: null, brand: DEFAULT_BRAND, loading: true, disabled: false, switchGym: () => {} });

export function TenantProvider({ children }) {
  const { user, claims, loading: authLoading } = useAuth();
  const [overrideGymId, setOverrideGymId] = useState(() => {
    try { return localStorage.getItem('tf:gymOverride'); } catch { return null; }
  });
  const initialSlug = typeof window !== 'undefined' ? getSubdomainGymSlug() : null;
  const initialCached = loadCachedSubdomainBrand(initialSlug);
  const [subdomainGym, setSubdomainGym] = useState(initialCached);
  const [state, setState] = useState(() => {
    if (initialCached) {
      return {
        gymId: initialCached.id,
        gym: initialCached,
        brand: { ...DEFAULT_BRAND, ...(initialCached.brand || {}) },
        disabled: initialCached.status === 'disabled',
        loading: true,
      };
    }
    return { gymId: null, gym: null, brand: DEFAULT_BRAND, loading: true };
  });

  const isPlatformAdmin = claims.role === 'platform-admin';
  // Priority: platform-admin override > auth claim > subdomain (pre-auth only)
  const effectiveGymId = (isPlatformAdmin && overrideGymId)
    ? overrideGymId
    : (claims.gymId || (user ? null : subdomainGym?.id));

  const switchGym = useCallback((newGymId) => {
    if (!isPlatformAdmin) return;
    setOverrideGymId(newGymId);
    try {
      if (newGymId) localStorage.setItem('tf:gymOverride', newGymId);
      else localStorage.removeItem('tf:gymOverride');
    } catch {}
  }, [isPlatformAdmin]);

  // Pre-auth: look up gym by subdomain slug for branded login/landing
  useEffect(() => {
    const slug = getSubdomainGymSlug();
    if (!slug) { setSubdomainGym(null); return; }
    let cancelled = false;
    getDocs(query(collection(db, 'gyms'), where('slug', '==', slug), limit(1))).then((snap) => {
      if (cancelled) return;
      if (!snap.empty) {
        const d = snap.docs[0];
        const gym = { id: d.id, ...d.data() };
        setSubdomainGym(gym);
        saveCachedSubdomainBrand(slug, gym);
      } else {
        // Fallback: try doc id directly (slug often equals id)
        const directRef = doc(db, 'gyms', slug);
        onSnapshot(directRef, (s) => {
          if (cancelled) return;
          if (s.exists()) {
            const gym = { id: s.id, ...s.data() };
            setSubdomainGym(gym);
            saveCachedSubdomainBrand(slug, gym);
          } else {
            setSubdomainGym(null);
          }
        }, () => setSubdomainGym(null));
      }
    }).catch(() => setSubdomainGym(null));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (authLoading) return;
    // No user + subdomain gym → use that for branding
    if (!user) {
      if (overrideGymId) {
        setOverrideGymId(null);
        try { localStorage.removeItem('tf:gymOverride'); } catch {}
      }
    }
    if (!user && subdomainGym) {
      setState({
        gymId: subdomainGym.id,
        gym: subdomainGym,
        brand: { ...DEFAULT_BRAND, ...(subdomainGym.brand || {}) },
        disabled: subdomainGym.status === 'disabled',
        loading: false,
      });
      return;
    }
    if (!user || !effectiveGymId) {
      setState({ gymId: effectiveGymId, gym: null, brand: DEFAULT_BRAND, loading: false });
      return;
    }
    const ref = doc(db, 'gyms', effectiveGymId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const gym = snap.exists() ? snap.data() : null;
        setState({
          gymId: effectiveGymId,
          gym,
          brand: { ...DEFAULT_BRAND, ...(gym?.brand || {}) },
          disabled: gym?.status === 'disabled',
          loading: false,
        });
      },
      () => setState({ gymId: effectiveGymId, gym: null, brand: DEFAULT_BRAND, loading: false }),
    );
    return unsub;
  }, [authLoading, user, effectiveGymId, subdomainGym]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', state.brand.primary);
    root.style.setProperty('--brand-accent', state.brand.accent);
  }, [state.brand]);

  return <TenantCtx.Provider value={{ ...state, switchGym }}>
    {state.disabled && !isPlatformAdmin ? <GymDisabledScreen brand={state.brand} /> : children}
  </TenantCtx.Provider>;
}

export function useTenant() { return useContext(TenantCtx); }
export function useBrand() { return useContext(TenantCtx).brand; }

function GymDisabledScreen({ brand }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 400 }}>
        <div className="wordmark" style={{ fontSize: '2rem', marginBottom: 12 }}>{(brand?.name || 'GYM').toUpperCase()}</div>
        <h2 style={{ margin: '0 0 12px' }}>This gym is currently inactive</h2>
        <p style={{ color: 'var(--text-dim)', marginBottom: 20 }}>
          Your account and workout history are preserved. Contact the gym owner or TrainForge support for assistance.
        </p>
        <button onClick={() => signOut(auth)} style={{ padding: '10px 24px' }}>
          Sign out
        </button>
      </div>
    </div>
  );
}
